import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../components/common/Toast';
import {
  getTeacherQuizzes,
  deleteSavedQuiz,
  duplicateQuiz,
  markQuizAsUsed,
  getTeacherFolders,
  searchQuizzes,
  type SavedQuiz
} from '../services/quizLibraryService';
import AssignmentDialog from '../components/dialogs/AssignmentDialog';
import EditQuizModal from '../components/dialogs/EditQuizModal';

export default function QuizLibrary() {
  const { user, userDoc } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  // State management
  const [quizzes, setQuizzes] = useState<SavedQuiz[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('all');
  const [selectedFolder, setSelectedFolder] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [folders, setFolders] = useState<string[]>([]);

  // Modal states
  const [selectedQuizForAssign, setSelectedQuizForAssign] = useState<SavedQuiz | null>(null);
  const [selectedQuizForEdit, setSelectedQuizForEdit] = useState<SavedQuiz | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);

  // Check if user is a teacher
  const isTeacher = userDoc?.role === 'teacher' || userDoc?.role === 'master' || userDoc?.role === 'masterAdmin';

  // Load quizzes
  const loadQuizzes = async () => {
    if (!user || !isTeacher) return;

    try {
      setLoading(true);
      const filters: any = {};

      if (selectedSubject !== 'all') filters.subject = selectedSubject;
      if (selectedStatus !== 'all') filters.status = selectedStatus;
      if (selectedFolder !== 'all') filters.folder = selectedFolder;

      let results: SavedQuiz[];

      if (searchTerm.trim()) {
        results = await searchQuizzes(user.uid, searchTerm);

        // Apply additional filters to search results
        if (selectedSubject !== 'all') {
          results = results.filter(q => q.subject === selectedSubject);
        }
        if (selectedStatus !== 'all') {
          results = results.filter(q => q.status === selectedStatus);
        }
        if (selectedFolder !== 'all') {
          results = results.filter(q => q.folder === selectedFolder);
        }
      } else {
        results = await getTeacherQuizzes(user.uid, filters);
      }

      setQuizzes(results);
    } catch (error) {
      console.error('Failed to load quizzes:', error);
      toast.error('Failed to load quiz library');
    } finally {
      setLoading(false);
    }
  };

  // Load folders
  const loadFolders = async () => {
    if (!user || !isTeacher) return;

    try {
      const teacherFolders = await getTeacherFolders(user.uid);
      setFolders(teacherFolders);
    } catch (error) {
      console.error('Failed to load folders:', error);
    }
  };

  // Effects
  useEffect(() => {
    if (user && isTeacher) {
      loadQuizzes();
      loadFolders();
    }
  }, [user, isTeacher, selectedSubject, selectedFolder, selectedStatus, searchTerm]);

  // Redirect non-teachers
  useEffect(() => {
    if (userDoc && !isTeacher) {
      toast.error("Access denied. Teachers only.");
      navigate('/dashboard');
    }
  }, [userDoc, isTeacher, navigate, toast]);

  // Handle delete quiz - FIXED VERSION 2024-12-24
  const handleDelete = async (quizId: string) => {
    try {
      await deleteSavedQuiz(quizId);
      setQuizzes(prev => prev.filter(q => q.id !== quizId));
      toast.success('Quiz deleted successfully');
    } catch (error: any) {
      console.error('Error deleting quiz:', error);
      // Safe error handling - never access undefined properties
      const errorMessage = error?.message || error?.toString() || 'Failed to delete quiz';
      toast.error(errorMessage);
    }
    setShowDeleteConfirm(null);
  };

  // Handle duplicate quiz
  const handleDuplicate = async (quiz: SavedQuiz) => {
    if (!user) return;

    try {
      const newQuizId = await duplicateQuiz(quiz.id, user.uid);
      toast.success(`"${quiz.name}" copied successfully`);
      loadQuizzes(); // Refresh the list
    } catch (error: any) {
      console.error('Error duplicating quiz:', error);
      const errorMessage = error?.message || 'Failed to copy quiz';
      toast.error(errorMessage);
    }
  };

  // Handle assign quiz
  const handleAssign = async (quiz: SavedQuiz) => {
    await markQuizAsUsed(quiz.id);
    setSelectedQuizForAssign(quiz);
  };

  // Handle edit quiz
  const handleEdit = (quiz: SavedQuiz) => {
    setSelectedQuizForEdit(quiz);
  };

  // Get unique subjects
  const subjects = [...new Set(quizzes.map(q => q.subject))].sort();

  if (!isTeacher && userDoc) {
    return null; // Will redirect
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50">
      <div className="container mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                📚 My Quizzes
              </h1>
              <p className="text-gray-600 mt-1">
                Manage your saved quizzes, templates, and reusable assessments
              </p>
            </div>
            <button
              onClick={() => navigate('/teacher/ai-builder')}
              className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 font-medium shadow-lg transition-all"
            >
              ✨ Create New Quiz
            </button>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-xl p-6 shadow-lg border border-purple-100 mb-8">
          <div className="flex flex-wrap gap-4 items-center">
            {/* Search */}
            <div className="flex-1 min-w-72">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search quizzes by name, topic, or tags..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
                <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400">
                  🔍
                </div>
              </div>
            </div>

            {/* Filters */}
            <div className="flex gap-3">
              <select
                value={selectedSubject}
                onChange={(e) => setSelectedSubject(e.target.value)}
                className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              >
                <option value="all">All Subjects</option>
                {subjects.map(subject => (
                  <option key={subject} value={subject}>{subject}</option>
                ))}
              </select>

              <select
                value={selectedFolder}
                onChange={(e) => setSelectedFolder(e.target.value)}
                className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              >
                <option value="all">All Folders</option>
                <option value="">No Folder</option>
                {folders.map(folder => (
                  <option key={folder} value={folder}>📁 {folder}</option>
                ))}
              </select>

              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              >
                <option value="all">All Status</option>
                <option value="draft">📝 Drafts</option>
                <option value="template">⭐ Templates</option>
                <option value="archived">🗄️ Archived</option>
              </select>
            </div>

            {/* View Mode Toggle */}
            <div className="flex bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode('grid')}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${
                  viewMode === 'grid'
                    ? 'bg-white text-purple-600 shadow-sm'
                    : 'text-gray-600 hover:text-purple-600'
                }`}
              >
                ⊞ Grid
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${
                  viewMode === 'list'
                    ? 'bg-white text-purple-600 shadow-sm'
                    : 'text-gray-600 hover:text-purple-600'
                }`}
              >
                ☰ List
              </button>
            </div>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="text-center py-12">
            <div className="inline-flex items-center gap-3 text-purple-600">
              <div className="w-6 h-6 border-2 border-purple-600 border-t-transparent rounded-full animate-spin"></div>
              Loading your quiz library...
            </div>
          </div>
        )}

        {/* Empty State */}
        {!loading && quizzes.length === 0 && (
          <div className="text-center py-16">
            <div className="text-6xl mb-4">📚</div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              {searchTerm ? 'No quizzes found' : 'Your quiz library is empty'}
            </h3>
            <p className="text-gray-600 mb-6">
              {searchTerm
                ? `No quizzes match "${searchTerm}". Try adjusting your search or filters.`
                : 'Create your first quiz using the AI Exam Builder to get started.'
              }
            </p>
            <div className="flex gap-4 justify-center">
              <button
                onClick={() => navigate('/teacher/ai-builder')}
                className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 font-medium shadow-lg transition-all"
              >
                ✨ Create First Quiz
              </button>
              {searchTerm && (
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setSelectedSubject('all');
                    setSelectedFolder('all');
                    setSelectedStatus('all');
                  }}
                  className="px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium transition-all"
                >
                  Clear Filters
                </button>
              )}
            </div>
          </div>
        )}

        {/* Quiz Grid/List */}
        {!loading && quizzes.length > 0 && (
          <>
            {/* Results Count */}
            <div className="flex items-center justify-between mb-6">
              <p className="text-gray-600">
                Found {quizzes.length} quiz{quizzes.length !== 1 ? 'es' : ''}
                {searchTerm && (
                  <> matching "<span className="font-medium">{searchTerm}</span>"</>
                )}
              </p>
            </div>

            {viewMode === 'grid' ? (
              // Grid View
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {quizzes.map((quiz) => (
                  <QuizCard
                    key={quiz.id}
                    quiz={quiz}
                    onEdit={() => handleEdit(quiz)}
                    onDelete={() => setShowDeleteConfirm(quiz.id)}
                    onDuplicate={() => handleDuplicate(quiz)}
                    onAssign={() => handleAssign(quiz)}
                  />
                ))}
              </div>
            ) : (
              // List View
              <div className="space-y-4">
                {quizzes.map((quiz) => (
                  <QuizListItem
                    key={quiz.id}
                    quiz={quiz}
                    onEdit={() => handleEdit(quiz)}
                    onDelete={() => setShowDeleteConfirm(quiz.id)}
                    onDuplicate={() => handleDuplicate(quiz)}
                    onAssign={() => handleAssign(quiz)}
                  />
                ))}
              </div>
            )}
          </>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl max-w-md w-full p-6 shadow-2xl">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Delete Quiz?</h3>
              <p className="text-gray-600 mb-6">
                Are you sure you want to delete this quiz? This action cannot be undone.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowDeleteConfirm(null)}
                  className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDelete(showDeleteConfirm)}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium transition-all"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Assignment Dialog */}
        {selectedQuizForAssign && (
          <AssignmentDialog
            isOpen={!!selectedQuizForAssign}
            onClose={() => setSelectedQuizForAssign(null)}
            quiz={{
              id: selectedQuizForAssign.id,
              title: selectedQuizForAssign.name,
              questions: selectedQuizForAssign.questions
            }}
            onAssignmentCreated={() => {
              toast.success('Assignment created successfully');
              setSelectedQuizForAssign(null);
            }}
          />
        )}

        {/* Edit Quiz Modal */}
        <EditQuizModal
          isOpen={!!selectedQuizForEdit}
          onClose={() => setSelectedQuizForEdit(null)}
          quiz={selectedQuizForEdit}
          teacherId={user?.uid || ''}
          onSaved={() => {
            loadQuizzes();
            setSelectedQuizForEdit(null);
          }}
        />
      </div>
    </div>
  );
}

// Quiz Card Component (Grid View)
function QuizCard({
  quiz,
  onEdit,
  onDelete,
  onDuplicate,
  onAssign
}: {
  quiz: SavedQuiz;
  onEdit: () => void;
  onDelete: () => void;
  onDuplicate: () => void;
  onAssign: () => void;
}) {
  const getStatusBadge = (status: string, isTemplate: boolean) => {
    if (isTemplate) return { icon: '⭐', text: 'Template', class: 'bg-yellow-100 text-yellow-800' };
    if (status === 'draft') return { icon: '📝', text: 'Draft', class: 'bg-blue-100 text-blue-800' };
    if (status === 'archived') return { icon: '🗄️', text: 'Archived', class: 'bg-gray-100 text-gray-800' };
    return { icon: '📄', text: 'Quiz', class: 'bg-green-100 text-green-800' };
  };

  const statusBadge = getStatusBadge(quiz.status, quiz.isTemplate);

  return (
    <div className="bg-white rounded-xl border border-purple-100 shadow-lg hover:shadow-xl transition-all p-6 group">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${statusBadge.class}`}>
              {statusBadge.icon} {statusBadge.text}
            </span>
            {quiz.folder && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-800 rounded-full text-xs font-medium">
                📁 {quiz.folder}
              </span>
            )}
          </div>
          <h3 className="font-bold text-lg text-gray-900 mb-2 line-clamp-2 group-hover:text-purple-600 transition-colors">
            {quiz.name}
          </h3>
          <div className="flex items-center gap-4 text-sm text-gray-600 mb-3">
            <span>📊 {quiz.totalQuestions} questions</span>
            <span>⏱️ {quiz.estimatedTime}min</span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="space-y-3 mb-4">
        <div className="flex items-center gap-2 text-sm">
          <span className="font-medium text-gray-900">{quiz.subject}</span>
          <span className="text-gray-400">•</span>
          <span className="text-gray-600">{quiz.gradeLevel}</span>
        </div>

        {quiz.topic && (
          <p className="text-sm text-gray-600">
            <span className="font-medium">Topic:</span> {quiz.topic}
          </p>
        )}

        {quiz.tags.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {quiz.tags.slice(0, 3).map(tag => (
              <span key={tag} className="inline-block px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs">
                #{tag}
              </span>
            ))}
            {quiz.tags.length > 3 && (
              <span className="inline-block px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs">
                +{quiz.tags.length - 3} more
              </span>
            )}
          </div>
        )}

        {quiz.description && (
          <p className="text-sm text-gray-600 line-clamp-2">
            {quiz.description}
          </p>
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between pt-4 border-t border-gray-100">
        <div className="text-xs text-gray-500">
          {quiz.timesUsed > 0 ? `Used ${quiz.timesUsed} times` : 'Never used'}
        </div>

        <div className="flex gap-2">
          <button
            onClick={onEdit}
            className="p-2 text-gray-600 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-all"
            title="Edit quiz"
          >
            ✏️
          </button>
          <button
            onClick={onDuplicate}
            className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
            title="Duplicate quiz"
          >
            📄
          </button>
          <button
            onClick={onDelete}
            className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
            title="Delete quiz"
          >
            🗑️
          </button>
          <button
            onClick={onAssign}
            className="px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all text-sm font-medium"
          >
            Assign
          </button>
        </div>
      </div>
    </div>
  );
}

// Quiz List Item Component (List View)
function QuizListItem({
  quiz,
  onEdit,
  onDelete,
  onDuplicate,
  onAssign
}: {
  quiz: SavedQuiz;
  onEdit: () => void;
  onDelete: () => void;
  onDuplicate: () => void;
  onAssign: () => void;
}) {
  const getStatusBadge = (status: string, isTemplate: boolean) => {
    if (isTemplate) return { icon: '⭐', text: 'Template', class: 'bg-yellow-100 text-yellow-800' };
    if (status === 'draft') return { icon: '📝', text: 'Draft', class: 'bg-blue-100 text-blue-800' };
    if (status === 'archived') return { icon: '🗄️', text: 'Archived', class: 'bg-gray-100 text-gray-800' };
    return { icon: '📄', text: 'Quiz', class: 'bg-green-100 text-green-800' };
  };

  const statusBadge = getStatusBadge(quiz.status, quiz.isTemplate);

  return (
    <div className="bg-white rounded-xl border border-purple-100 shadow-lg hover:shadow-xl transition-all p-6">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <h3 className="font-bold text-lg text-gray-900 hover:text-purple-600 transition-colors">
              {quiz.name}
            </h3>
            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${statusBadge.class}`}>
              {statusBadge.icon} {statusBadge.text}
            </span>
            {quiz.folder && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-800 rounded-full text-xs font-medium">
                📁 {quiz.folder}
              </span>
            )}
          </div>

          <div className="flex items-center gap-6 text-sm text-gray-600">
            <span><span className="font-medium">{quiz.subject}</span> • {quiz.gradeLevel}</span>
            <span>📊 {quiz.totalQuestions} questions</span>
            <span>⏱️ {quiz.estimatedTime}min</span>
            <span>{quiz.timesUsed > 0 ? `Used ${quiz.timesUsed} times` : 'Never used'}</span>
          </div>

          {(quiz.topic || quiz.description) && (
            <div className="mt-2 text-sm text-gray-600">
              {quiz.topic && <span><span className="font-medium">Topic:</span> {quiz.topic}</span>}
              {quiz.topic && quiz.description && <span className="mx-2">•</span>}
              {quiz.description && <span>{quiz.description}</span>}
            </div>
          )}

          {quiz.tags.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {quiz.tags.map(tag => (
                <span key={tag} className="inline-block px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs">
                  #{tag}
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 ml-6">
          <button
            onClick={onEdit}
            className="p-2 text-gray-600 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-all"
            title="Edit quiz"
          >
            ✏️
          </button>
          <button
            onClick={onDuplicate}
            className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
            title="Duplicate quiz"
          >
            📄
          </button>
          <button
            onClick={onDelete}
            className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
            title="Delete quiz"
          >
            🗑️
          </button>
          <button
            onClick={onAssign}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all font-medium"
          >
            Assign
          </button>
        </div>
      </div>
    </div>
  );
}